/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */

import { FileInfoPair } from "../../Api/DataTransformation";
import { FileType } from "../../Api/Api";

/**
 * Coerce a raw string (case-insensitive) into the FileType enum, falling back to UNDETECTED.
 */
export function toFileTypeEnum(raw: any): FileType {
  if (typeof raw !== "string") return FileType.UNDETECTED;
  const upper = raw.toUpperCase();
  return (FileType as any)[upper] !== undefined
    ? (FileType as any)[upper]
    : FileType.UNDETECTED;
}

/**
 * Normalize various incarnations of mappingResult into either a proper FileInfoPair,
 * a fallback string, or null.
 */
export function normalizeRawMappingResult(
  raw: any
): string | FileInfoPair | null {
  if (!raw) return null;

  if (typeof raw === "string") {
    const contentMatch = raw.match(/content=(.*?),\s*fileType=/s);
    const typeMatch = raw.match(/fileType=([A-Za-z0-9_]+)/);
    const fileNameMatch = raw.match(/fileName=([^\)\n\r]+)/);

    if (contentMatch && typeMatch && fileNameMatch) {
      const content = contentMatch[1].trim();
      const fileName = fileNameMatch[1];
      const fileTypeStr = typeMatch[1];
      const fileTypeEnum = toFileTypeEnum(fileTypeStr);
      return { content, fileType: fileTypeEnum, fileName } as FileInfoPair;
    }

    // not matching expected legacy format; return original string so callers can decide
    return raw;
  }

  if (typeof raw === "object" && raw !== null) {
    if ("content" in raw && "fileName" in raw && "fileType" in raw) {
      const content = (raw as any).content;
      const rawFileType = (raw as any).fileType;
      const fileName = (raw as any).fileName;
      const fileTypeEnum =
        typeof rawFileType === "string"
          ? toFileTypeEnum(rawFileType)
          : rawFileType;

      return { content, fileType: fileTypeEnum, fileName } as FileInfoPair;
    }
  }

  return null;
}

/**
 * Type guard for FileInfoPair.
 *
 * Basically checks if value is an object and if it matches the shape of a FileInfoPair,
 * i.e., does it have a content property and a fileType property that is a member of the
 * FileType enum?
 */
export function isFileInfoPair(candidate: unknown): candidate is FileInfoPair {
  if (typeof candidate !== "object" || candidate === null) return false;

  const potentialPair = candidate as { content?: unknown; fileType?: unknown };

  if (typeof potentialPair.content !== "string") return false;
  if (!("fileType" in potentialPair)) return false;

  // ensure the fileType value is one of the enum values
  return Object.values(FileType).includes(potentialPair.fileType as FileType);
}
